import Head from "next/head";

// ** API
import articlesApi from "@/api/articles/api";
import technologiesApi from "@/api/technologies/api";

// ** Components
import CategoryComponent from "../../components/CategoryComponent";

// ** MUI
import { Container, Grid2 as Grid, Typography } from "@mui/material";

// * Layout
import DefaultLayout from "@/layout/DefaultLayout";

function Blog({ technologiesWithArticles }) {
  const siteUrl = "https://valentinmarko.com";

  const canonicalUrl = `${siteUrl}/blog`;

  const title = `Blog | Valentin-Marko – Software Development Blog`;

  const description =
    "Explore the latest articles on React, JavaScript, TypeScript, and more. Tutorials, tips, and best practices from Valentin-Marko's Software Development Blog.";

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:site_name"
          content="Valentin-Marko – Software Development Blog"
        />
        <meta
          property="og:image"
          content="https://valentinmarko.com/about.webp"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://valentinmarko.com/#website",
                name: "Valentin Marko – Software Development Blog",
                url: "https://valentinmarko.com/home",
              },
              {
                "@type": "Blog",
                "@id": "https://valentinmarko.com/#blog",
                name: "Valentin Marko – Software Development Blog",
                url: "https://valentinmarko.com/blog",
                description: description,
                publisher: { "@id": "https://valentinmarko.com/#person" },
              },
              {
                "@type": "CollectionPage",
                "@id": "https://valentinmarko.com/blog#webpage",
                name: title,
                url: canonicalUrl,
                description: description,
                isPartOf: { "@id": "https://valentinmarko.com/#website" },
              },
            ],
          }),
        }}
      />

      <Container sx={{ minHeight: "calc(100vh - 150px)" }}>
        <Grid container spacing={3} marginTop={"34px"} marginBottom={"34px"}>
          <Typography
            component={"h1"}
            sx={{
              fontSize: { xs: "25px", sm: "35px", lg: "44px" },
              fontWeight: 900,
              color: "#2D3748",
              textTransform: "capitalize",
            }}
          >
            Blog Articles by Technology
          </Typography>
          {technologiesWithArticles.map((technologyWithArticles) => (
            <CategoryComponent
              key={technologyWithArticles.technology.id}
              technologyWithArticles={technologyWithArticles}
            />
          ))}
        </Grid>
      </Container>
    </>
  );
}

export async function getStaticProps() {
  // Set pagination (first 3 articles)
  const page = 1;
  const pageSize = 4;

  const technologies = await technologiesApi.getTechnologies();

  // Use map to create an array of promises
  const articlesPromises = technologies.data.map(async (technology) => {
    const articlesRes = await articlesApi.getPaginatedArticlesByTechnology(
      technology.slug,
      page,
      pageSize
    );

    return { technology, articles: articlesRes.data };
  });

  // Wait for all promises to resolve
  const technologiesWithArticles = await Promise.all(articlesPromises);

  return {
    props: { technologiesWithArticles }, // Pass articles as props
    revalidate: 10, // Re-generate the page every 10 seconds
  };
}

Blog.getLayout = (page) => <DefaultLayout>{page}</DefaultLayout>;

export default Blog;
